import { getTrackingParams } from './tracking'
import { isAnnualPlan } from './storage'
import { getEfTransactionId } from './everflow'
import { applyProtection, applyProtectionParams } from './variation'

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL

// Mirrors the LP_SOURCE constant in utils/api.js (duplicated rather than shared,
// same as createAccount.jsx / loginAccount.jsx already do).
const LP_SOURCE = process.env.REACT_APP_VERCEL_ENV ? 'createsexfriend' : 'swipey.ai/createsexfriend'

// Resolve the post-auth destination path from the GB
// `createsexfriend-paywall-nav-config` flag. `chat_paywall` sends the user to
// /lobby; anything else (incl. the default `paywall` or a missing value) keeps
// the existing /paywall behaviour. The rest of the query string — auth_token,
// model-params, tracking, theme, plans — is built identically either way.
// `duel_quiz` (123mehfujvu) sends the user to the platform's duel quiz instead —
// see isDuelQuiz / buildDuelQuizRedirect in utils/api.js for its (character-free)
// query-string contract.
export const resolvePaywallPath = navType =>
  navType === 'chat_paywall' ? '/lobby' : navType === 'duel_quiz' ? '/duel/quiz' : '/paywall'

// True for the duel_quiz arm — the picked character must never ride the
// redirect (selected_slug / model-params / type / theme / plans / protection),
// only the lander's usual attribution (utm/click-ids, ef_transaction_id,
// lp_source) plus the one-time login_pre_lander hand-off.
export const isDuelQuiz = navType => navType === 'duel_quiz'

// "Popup first" A/B variant. The GB `createsexfriend-paywall-nav-config` flag
// keeps `type: 'paywall_popup'` (so every backend / platform branch that checks
// that literal stays true) and rides the ordering in a separate `order` field:
// `order: 'popup_first'` shows the paywall popup BEFORE registration.
export const isPopupFirst = order => order === 'popup_first'

// Platform-global rebill experiment (the backend seeds it on the user as the
// `exp-full-rebill` experiment at signup). 'flat_rebill' rebills at the amount
// paid today; 'control' steps up to the full price. Read from GrowthBook because
// GET /subscription-plans only fills `plan.isFlatRebill` for a cookie-authenticated
// user, and LP traffic is cross-origin with no cookie.
export const FULL_REBILL_FLAG = 'exp_full_rebill'
export const FLAT_REBILL_VARIANT = 'flat_rebill'
export const FULL_REBILL_CONTROL = 'control'

export const isFlatRebillArm = variant => variant === FLAT_REBILL_VARIANT

// Geo pricing. The backend multiplies the plan up from
// `user.marketing.experiments['location-based-price-multiplier']` when it builds
// the Upgate charge, but GET /subscription-plans only applies it for an
// authenticated user — and LP traffic is cross-origin with no cookie, so the
// plans we render carry the BASE prices. Any amount the LP prints in the
// checkout text therefore has to be multiplied here or it understates what the
// user is actually billed.
export const LOCATION_PRICE_MULTIPLIER_FLAG = 'location-based-price-multiplier'
export const DEFAULT_PRICE_MULTIPLIER = 1

// The GB flag is number-typed. Still coerced and range-checked rather than used
// raw: an unset flag arrives as undefined, and a non-positive value would zero
// out or invert a price, so anything not > 0 falls back to 1.
export const resolvePriceMultiplier = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_PRICE_MULTIPLIER
}

// MUST match the backend's shared/utils/applyMultiplier exactly — it is what
// computes the amount actually charged. Diverging rounding here would print a
// price the user is never billed.
export const applyPriceMultiplier = (price, multiplier = DEFAULT_PRICE_MULTIPLIER) => {
  if (!price) return price
  return Math.ceil(price * multiplier) - 0.01
}

// Analytics arm label. Rides the `paywall_type` URL param onto the /lobby return
// (backend forwards it verbatim — ALLOWED_REDIRECT_PARAMS filters on name only)
// and the LP's own order_created events, so the popup-first cohort separates in
// GTM/Mixpanel even though the backend nav `type` stays 'paywall_popup'.
export const popupTypeLabel = order =>
  isPopupFirst(order) ? 'paywall_popup_first' : 'paywall_popup'

// Backend reads only `path` and `model-params` from the Google OAuth state
// and ignores everything else. To get plans/tracking onto the post-OAuth
// paywall URL without a backend change, we encode them into `path` itself —
// backend builds the final URL via `new URL(BASE + path)`, which parses the
// embedded query string correctly.
export const buildOAuthRedirectPath = (postSignupRedirect, selections, theme, navType) => {
  const params = new URLSearchParams()

  // duel_quiz carries no character: skip plans/type/theme/protection below and
  // return early with only tracking + ef_transaction_id, the same attribution
  // every other arm forwards.
  if (isDuelQuiz(navType)) {
    // /duel/quiz reads its arriving `lp_source` query param as `entry_lp_source`
    // (design decision 10) — this has to ride inside `path`'s own query string,
    // since that's what the backend's getRedirectUrl actually lands the browser
    // on for this arm.
    params.set('lp_source', LP_SOURCE)
    const efTransactionId = getEfTransactionId()
    if (efTransactionId) params.set('ef_transaction_id', efTransactionId)
    const trackingParams = getTrackingParams()
    Object.entries(trackingParams).forEach(([key, value]) => {
      if (value) params.set(key, value)
    })
    const queryString = params.toString()
    return queryString ? `${resolvePaywallPath(navType)}?${queryString}` : resolvePaywallPath(navType)
  }

  if (isAnnualPlan() || postSignupRedirect === 'annual') {
    params.set('plans', 'annual')
  }

  if (selections?.welcome) {
    params.set('type', selections.welcome === 'Anime' ? 'anime' : 'realistic')
  }

  if (theme) params.set('theme', theme)

  const efTransactionId = getEfTransactionId()
  if (efTransactionId) params.set('ef_transaction_id', efTransactionId)

  const trackingParams = getTrackingParams()
  Object.entries(trackingParams).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })

  // chat_paywall → /lobby: set type=chat_paywall LAST so it wins over the style type AND the
  // tracking spread above — getTrackingParams() forwards the landing URL's ?type=, which would
  // otherwise clobber it back to realistic/anime. /lobby reads style from model-params.
  if (navType === 'chat_paywall') params.set('type', 'chat_paywall')

  // SFW arm (?variation=sfw): the backend re-parses `path` as a query string, so
  // the protection marker has to be encoded INTO it — appending it to the outer
  // OAuth URL would be dropped. No-op on the NSFW arm.
  applyProtectionParams(params)

  const navPath = resolvePaywallPath(navType)
  const queryString = params.toString()
  return queryString ? `${navPath}?${queryString}` : navPath
}

export const buildPaywallUrl = (
  authToken,
  authType = 'login_paywall',
  modelParams = null,
  style = 'Realistic',
  model,
  pw,
  halloween,
  theme,
  postSignupRedirect,
  navType,
) => {
  const paywallUrl = new URL(SWIPEY_BASE_URL + resolvePaywallPath(navType))

  paywallUrl.searchParams.set('auth_token', authToken)
  paywallUrl.searchParams.set('auth_type', authType)

  const typeValue = style === 'Anime' ? 'anime' : 'realistic'
  paywallUrl.searchParams.set('type', typeValue)

  const efTransactionId = getEfTransactionId()
  if (efTransactionId) {
    paywallUrl.searchParams.set('ef_transaction_id', efTransactionId)
  }

  const trackingParams = getTrackingParams()
  Object.entries(trackingParams).forEach(([key, value]) => {
    if (value) paywallUrl.searchParams.set(key, value)
  })

  if (modelParams) paywallUrl.searchParams.set('model-params', modelParams)
  if (model) {
    paywallUrl.searchParams.set('media', model)
    paywallUrl.searchParams.set('pw_v', '1')
  }
  if (pw) {
    paywallUrl.searchParams.set('media', 'vid.mp4')
    paywallUrl.searchParams.set('pw_v', pw)
  }
  if (halloween) paywallUrl.searchParams.set('media', halloween)
  if (theme) paywallUrl.searchParams.set('theme', theme)

  // /12 route already forces annual; otherwise GB flag can opt traffic into it.
  // Why: experiment is scoped out on /12 by product decision.
  const annualByRoute = isAnnualPlan()
  const annualByFlag = !annualByRoute && postSignupRedirect === 'annual'
  if (annualByRoute || annualByFlag) paywallUrl.searchParams.set('plans', 'annual')

  // chat_paywall → /lobby: set type=chat_paywall LAST so it wins over the style type AND the
  // tracking spread above — getTrackingParams() forwards the landing URL's ?type=, which would
  // otherwise clobber it back to realistic/anime. /lobby reads style from model-params.
  if (navType === 'chat_paywall') paywallUrl.searchParams.set('type', 'chat_paywall')

  // SFW arm (?variation=sfw) → the platform serves the protected experience.
  applyProtection(paywallUrl)

  return paywallUrl.toString()
}
